<template>
  <div class="catalog-view" :class="{ 'sidebar-collapsed': leftSidebarCollapsed }" v-if="canRead">
    <div class="left-sidebar">
      <div class="toggle-button d-flex justify-content-center align-items-center" @click="leftSidebarCollapsed = !leftSidebarCollapsed">
        <i class="fas fa-caret-right" v-if="leftSidebarCollapsed"></i>
        <i class="fas fa-caret-left" v-else></i>
      </div>
      <div class="catalog-name">
        <i class="fad fa-mobile"></i>
        <span>Dispositivos</span>
      </div>

      <!-- FILTERS AND VIEWS -->
      <div class="filters" v-if="currentAction === crudActions.READ">
        <div class="filter-items">
          <div class="filter-item">
            <label for="order-filter" class="small">Ordenar por</label>
            <b-form-group name="order-filter" class="mb-0">
              <b-form-select class="mb-1" v-model="filters.sort" :options="orderOptions" size="sm"></b-form-select>
              <b-form-radio class="mr-1" size="sm" button v-model="filters.sortOrder" button-variant="light" value="asc">Asc</b-form-radio>
              <b-form-radio class="mr-1" size="sm" button v-model="filters.sortOrder" button-variant="light" value="desc">Desc</b-form-radio>

              <div id="order-select-info" class="float-right mr-1 mt-1"><i class="fal fa-info-circle inline"></i></div>
              <b-tooltip target="order-select-info" triggers="click">
                Indica si el ordenamiento de los registros será de forma ascendente o descendente con base en su campo de orden
              </b-tooltip>
            </b-form-group>
          </div>

          <h3>Filtros de búsqueda</h3>
          <div class="filter-item">
            <label>Nombre</label>
            <b-form-input class="shadow-none" placeholder="Buscar ..."
              v-model.trim="filters.name"
              @keyup.enter="filterItems">
            </b-form-input>
          </div>

          <div class="filter-item">
            <label>UUID</label>
            <b-form-input class="shadow-none" placeholder="UUID del dispositivo ..."
              v-model.trim="filters.uuid"
              @keyup.enter="filterItems">
            </b-form-input>
          </div>

          <div class="filter-item">
            <label>Estado</label>
            <b-form-radio-group v-model="filters.status" stacked>
              <b-form-radio size="lg" :value="deviceStatus.active">Activo</b-form-radio>
              <b-form-radio size="lg" :value="deviceStatus.inactive">Inactivo</b-form-radio>
              <b-form-radio size="lg" :value="deviceStatus.resettle">Eliminado</b-form-radio>
            </b-form-radio-group>
          </div>
        </div>

        <b-row class="main-actions">
          <b-overlay
            class="main-actions__overlay"
            spinner-type="grow"
            spinner-small
            :show="loadingData">
            <b-col cols="6">
              <b-button block size="sm" class="shadow-sm" @click="cleanFilter" :disabled="loadingData">Limpiar</b-button>
            </b-col>
            <b-col cols="6">
              <b-button variant="primary" block size="sm" class="shadow-sm" @click="filterItems" :disabled="loadingData">Buscar</b-button>
            </b-col>
          </b-overlay>
        </b-row>
      </div>

      <div class="short-list" v-if="currentAction !== crudActions.READ">
        <h5>Registros:</h5>
        <b-list-group>
          <b-list-group-item
            class="text-truncate"
            v-for="item in itemList"
            :title="item.id + ' - ' + item.name"
            :key="item.id"
            :active="selectedItem == item"
            @click="selectItem(item)">
            {{ item.name }} <span v-if="item.user" style="float: right"><i class="fal fa-id-badge"></i></span>
          </b-list-group-item>
        </b-list-group>
      </div>
    </div>

    <div class="right-side">
      <div class="grid">
        <!-- TOP BUTTONS BAR -->
        <div class="top-tool-bar">
          <div class="action d-none d-xl-block" @click="queryItem" :class="{ disabled: !selectedItem.id }"><i class="fal fa-print-search"></i> Detalle</div>
          <div class="action d-none d-xl-block" @click="showResetDeviceModal" v-if="canDeleteDevices" :class="{ disabled: !selectedItem.id || !isActiveDevice || !isMobileDevice }"><i class="fal fa-skull"></i> Eliminar dispositivo</div>
          <div class="action d-none d-xl-block" @click="showUnlockLicensesModal" v-if="canDeleteLicenses" :class="{ disabled: !selectedItem.id || !isActiveDevice }"><i class="fal fa-book-dead"></i> Liberar licencias</div>
          <div class="action d-none d-xl-block" @click="showLicenses" v-if="canReadLicenses" :class="{ disabled:  !selectedItem.id}"><i class="fal fa-id-badge"></i> Historial</div>
          <div class="action d-none d-xl-block" title="Descargar registros" @click="exportData"><i class="fal fa-file-spreadsheet"></i> Exportar</div>
          <div class="action d-none d-xl-block" title="Refrescar tabla para obtener las últimas actualizaciones" @click="loadData(true)"><i class="fal fa-sync"></i> Refrescar</div>

          <div class="actions-collapsed d-xl-none">
            <div class="action d-inline" title="Detalle del registro" @click="queryItem" :class="{ disabled: !selectedItem.id }"><i class="fal fa-print-search"></i></div>
            <div class="action d-inline" title="Eliminar dispositivo" @click="showResetDeviceModal" v-if="canDeleteDevices" :class="{ disabled: !selectedItem.id || !isActiveDevice }"><i class="fal fa-skull"></i></div>
            <div class="action d-inline" title="Liberar licencias" @click="showUnlockLicensesModal" v-if="canDeleteLicenses" :class="{ disabled: !selectedItem.id || !isActiveDevice }"><i class="fal fa-book-dead"></i></div>
            <div class="action d-inline" title="Historial" @click="showLicenses" v-if="canReadLicenses" :class="{ disabled:  !selectedItem.id}"><i class="fal fa-id-badge"></i></div>
            <div class="action d-inline" title="Descargar registros" @click="exportData"><i class="fal fa-file-spreadsheet"></i></div>
            <div class="action d-inline" title="Refrescar tabla para obtener las últimas actualizaciones" @click="loadData(true)"><i class="fal fa-sync"></i></div>
          </div>

          <TopToolBarPaginator
            :key="paginatorKey"
            :totalItems="totalItems"
            :pageSize="PAGE_SIZE_VISIBLE_ITEMS"
            @pageChange="onPageChange"
            v-b-tooltip.hover.left="'Total de registros: ' + totalItems"/>
        </div>

        <!-- DATA TABLE -->
        <div class="table-container">
          <table class="data-table table table-hover">
            <thead>
              <tr>
                <template v-for="(col, index) in columns">
                  <th :key="index">
                    <div>
                      {{ col.name }}
                    </div>

                    <div v-if="col.sort" class="sort" @click="sortItems(col)">
                      <i class="fad fa-sort"></i>
                    </div>
                  </th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in itemList" :key="item.id" @click="selectItem(item)" @dblclick="selectItemAndShowDetail(item)" :class="{ 'selected': isItemSelected(item) }" v-show="isRowVisibleInPage(index)">
                <template v-for="(col, index) in columns">
                  <template v-if="index === 0">
                    <th :key="index" style="max-width: 225px;" class="text-truncate" :title="item[col.key]">
                      {{ item[col.key] }}
                    </th>
                  </template>
                  <template v-else-if="col.key === 'user'">
                    <td :key="index">
                      <b-overlay :show="item.loading" spinner-small>
                        <div :title="item[col.key] && item[col.key].length > 40 ? item[col.key] : ''">{{ item[col.key] }}</div>
                      </b-overlay>
                    </td>
                  </template>
                  <template v-else>
                    <td :key="index">
                      <div :title="item[col.key] && item[col.key].length > 40 ? item[col.key] : ''">{{ item[col.key] }}</div>
                    </td>
                  </template>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- CONSULTA DE DETALLE -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.READ_DETAIL">
          <AdminDeviceForm :key="selectedItem.id" :id="selectedItem.id" :user="selectedItem.user" :title="selectedItem.name" :readOnly="true" :canRefresh="canRead" :canDelete="canDeleteDevices && isActiveDevice" @close="onCancelForm" @delete-item="showResetDeviceModal"/>
        </div>
      </transition>

      <!-- HISTORIAL DE LICENCIAS -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.LICENSES">
          <AdminLicensesList
            :device="selectedItem"
            :key="selectedItem.id"
            :domainsFilter="canReadDomains"
            :usersFilter="canReadUsers"
            :releaseLicensesButton="canDeleteLicenses"
            :updateComponent="updateAdminLicensesList"
            :resetDevicesButton="canDeleteDevices"
            @close="onCancelForm"
            @release-licenses="showDeleteLicensesModal"
            @reset-devices="showDeleteDeviceModal"
            @updated-component="updateAdminLicensesList = false"
          />
        </div>
      </transition>
      <!-- LIBERAR (ELIMINAR) LICENCIAS -->
      <ReleaseLicensesModal
        :licenses="deviceLicensesToRelease"
        :fromAdminLicensesList="fromAdminLicensesList"
        @update-licenses-list="licensesReleased"
      />
      <!-- ELIMINACIÓN DE DISPOSITIVOS -->
      <ResetLicenseDeviceModal
        :devices="devicesToUnlock"
        @update-devices-list="deviceReseted"
      />

      <!-- LOADING MASK -->
      <div class="loading-mask" v-show="loadingData">
        <i class="far fa-spinner-third fa-spin"></i>
      </div>
    </div>

    <a class="hidden-csv-download-link" ref="csvDownload"></a>
  </div>
  <div v-else>
    <div id="not-sufficent-permissions" class="page-wrap d-flex flex-row align-items-center">
        <div class="container">
          <div class="row justify-content-center">
              <div class="col-md-12 text-center">
                  <span class="display-1 d-block">403</span>
                  <div class="mb-4 lead">Lo sentimos, no tienes permisos suficientes para consultar estos registros.</div>
              </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import catalogsService from '@/services/CatalogsService'
import { CrudActions, CatalogGridItemColumn } from '@/types/Catalogs'
import { DeviceCatalogFilters, DeviceCatalogGridItem, DeviceStatus, Device } from '@/types/catalogs/Devices'
import AdminDeviceForm from '@/components/admin/AdminDeviceForm.vue'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import { PageRequest } from '@/types/Support'
import userInfoStore from '@/store/userInfo'
import { PermissionNames } from '@/types/Auth'
import reportsService from '@/services/ReportsService'
import { License, LicenseFilters } from '@/types/catalogs/Licenses'
import InputSelectorFilter from '@/components/generic/InputSelectorFilter.vue'
import AdminLicensesList from '@/components/admin/AdminLicensesList.vue'
import ReleaseLicensesModal from '@/components/admin/users/ReleaseLicensesModal.vue'
import ResetLicenseDeviceModal from '@/components/admin/users/ResetLicenseDeviceModal.vue'
import DateFormat from '@/services/DateFormat'

@Component({
  components: {
    TopToolBarPaginator,
    AdminDeviceForm,
    InputSelectorFilter,
    AdminLicensesList,
    ReleaseLicensesModal,
    ResetLicenseDeviceModal
  }
})
export default class AdminDevices extends Vue {
  MOBILE_UUID_SIZE = 16
  PAGE_SIZE = 500
  PAGE_SIZE_VISIBLE_ITEMS = 50
  itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
  currentPage = 1
  paginatorKey = 0
  deviceStatus = DeviceStatus
  crudActions = CrudActions
  currentAction = CrudActions.READ
  loadingData = false
  leftSidebarCollapsed = false
  // Grid columns definition
  columns: CatalogGridItemColumn[] = [
    CatalogGridItemColumn.def('id', 'ID', true),
    CatalogGridItemColumn.def('name', 'Nombre', true),
    CatalogGridItemColumn.def('status', 'Estado', true),
    CatalogGridItemColumn.def('uuid', 'UUID'),
    CatalogGridItemColumn.def('appVersion', 'Versión app'),
    CatalogGridItemColumn.def('osVersion', 'Sistema operativo'),
    CatalogGridItemColumn.def('creation', 'Fecha de creación'),
    CatalogGridItemColumn.def('update', 'Fecha de actualización')
  ]
  orderOptions = [
    { value: 'id', text: 'ID' },
    { value: 'name', text: 'Nombre' },
    { value: 'creationDate', text: 'Fecha de creación' },
    { value: 'updateDate', text: 'Fecha de actualización' }
  ]
  allDeviceItems: DeviceCatalogGridItem[] = []
  items: DeviceCatalogGridItem[] = []
  totalItems: number = 0
  filteredItems: DeviceCatalogGridItem[] = []
  filters = new DeviceCatalogFilters()
  selectedItem = new DeviceCatalogGridItem()
  selectedItemAsDevice = new Device()
  devicesToUnlock: Device[] = []
  deviceLicensesToRelease: License[] = []
  fromAdminLicensesList: boolean = false
  updateAdminLicensesList: boolean = false

  created () {
    // Add the user column after uuid column
    if (this.canReadLicenses) {
      this.columns.splice(4, 0, CatalogGridItemColumn.def('user', 'Usuarios (Activos)'))
    }
    this.loadData(true)
  }

  /* User permissions */

  /**
   * Check if user has 'permission' permission
   * @param permission name to check
   */
  hasPermission (permision: string) {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === permision)
  }

  /**
   * Checks if user can read the records
   */
  get canRead () {
    return this.hasPermission(PermissionNames.DEVICES_READ)
  }

  /**
   * Checks if user can delete a record
   */
  get canDeleteDevices () {
    return this.hasPermission(PermissionNames.DEVICES_DELETE)
  }

  /**
   * Checks if user can read licenses
   */
  get canReadLicenses () {
    return this.hasPermission(PermissionNames.LICENSES_READ)
  }

  /**
   * Checks if user can delete licenses
   */
  get canDeleteLicenses () {
    return this.hasPermission(PermissionNames.LICENSES_DELETE)
  }

  /**
   * Checks if user can read domains
   */
  get canReadDomains () {
    return this.hasPermission(PermissionNames.DOMAINS_READ)
  }

  /**
   * Checks if user can read users
   */
  get canReadUsers () {
    return this.hasPermission(PermissionNames.USERS_READ)
  }

  /**
   * @returns true if the current device selected is active
   */
  get isActiveDevice () {
    return this.hasStatusDevice(this.selectedItem, this.deviceStatus.active)
  }

  /**
   * @returns true if the current grid item has the status as parameter
   */
  hasStatusDevice (deviceItem: DeviceCatalogGridItem, status: DeviceStatus) {
    return deviceItem.status === Device.getStatus(status)
  }

  /**
   * Based on the uuid size of the device
   * we know if is a mobile or a browser
   *
   * @returns true if the UUID size is less than 16 chars
   */
  get isMobileDevice () {
    return (this.selectedItem.uuid.length <= this.MOBILE_UUID_SIZE) ||
      (this.selectedItem.appVersion !== undefined && this.selectedItem.osVersion !== undefined)
  }

  /** @returns the current items list */
  get itemList () {
    return this.items
  }

  /* Load data */

  /** @returns a page request with the configurations to get the device records */
  devicePageRequest () {
    let pageRequest = new PageRequest<DeviceCatalogFilters>(this.PAGE_SIZE_VISIBLE_ITEMS)
    pageRequest.sort = this.filters.sort
    pageRequest.sortOrder = this.filters.sortOrder
    pageRequest.filters = this.filters
    pageRequest.page = this.currentPage - 1
    return pageRequest
  }

  /** Manage load records */
  loadData (updateTotalItems: boolean) {
    if (this.loadingData) return
    this.cleanItemSelected()
    this.items = []
    let pageRequest = this.devicePageRequest()
    this.loadDevices(pageRequest, updateTotalItems)
  }

  /**
   * GETs all devices with base on a page request
   * @param pageRequest - with config to GET devices
   */
  async loadDevices (pageRequest: PageRequest<DeviceCatalogFilters>, updateTotalItems: boolean) {
    this.loadingData = true
    catalogsService.getDevices(pageRequest)
      .then((response) => {
        if (updateTotalItems) {
          this.totalItems = response.total
        }
        this.items.push(...response.results.map((it: any) => {
          return this.fillGridItem(it, new DeviceCatalogGridItem())
        }))
      }).catch((error) => {
        let messageValues = {
          title: 'Error al cargar dispositivos',
          message: '',
          delay: 4000,
          error: error
        }
        new MessageError({ propsData: messageValues }).displayMessage()
      }).finally(() => {
        this.loadingData = false
        if (this.canReadLicenses) {
          this.fillUserGridColumn(0, 10)
        }
      })
  }

  /**
   * Gets all devices that exist
   * @param pageRequest - with config to GET devices
   */
  async loadAllDevices (pageRequest: PageRequest<DeviceCatalogFilters>) {
    await catalogsService.getDevices(pageRequest)
      .then((response) => {
        this.allDeviceItems.push(
          ...response.results.map((it: any) => {
            return this.fillGridItem(it, new DeviceCatalogGridItem())
          }))
      }).catch((error) => {
        let messageValues = {
          title: 'Error al cargar dispositivos',
          message: '',
          delay: 4000,
          error: error
        }
        new MessageError({ propsData: messageValues }).displayMessage()
      })
    if (this.allDeviceItems.length < this.totalItems) {
      pageRequest.page++
      await this.loadAllDevices(pageRequest)
    }
  }

  /**
   * Gets all the licenses related to a device
   *
   * @param device to get by
   * @param pageRequest with request configurations
   */
  async getLicensesFromDevice (device: DeviceCatalogGridItem, pageRequest: PageRequest<LicenseFilters>) {
    Vue.set(device, 'loading', true)
    await catalogsService.getLicenses(pageRequest)
      .then((response) => {
        device.activeLicenses.push(...response.results)
        let activeLicensesUsers = device.activeLicenses.map(license => { return license.user.name })
        activeLicensesUsers && activeLicensesUsers.length > 0
          ? Vue.set(device, 'user', activeLicensesUsers.join(', '))
          : Vue.set(device, 'user', '')
      })
      .finally(() => {
        Vue.set(device, 'loading', false)
      })
  }

  /**
   * Generates page with configurations to get all licenses by device.
   *
   * @param deviceId to get licenses
   */
  prepareRequestForActiveLicenses (deviceId: number) {
    let pageRequest = new PageRequest<LicenseFilters>(this.PAGE_SIZE_VISIBLE_ITEMS)
    pageRequest.filters.deviceId = deviceId
    pageRequest.filters.active = true
    return pageRequest
  }

  /**
   * Gets the licenses with the pageRequest configurations
   *
   * @param pageRequest with query-parameters and/or filters
   */
  async getLicenses (pageRequest: PageRequest<LicenseFilters>) {
    let licenses: License[] = []
    await catalogsService.getLicenses(pageRequest)
      .then((response) => {
        licenses.push(...response.results)
      })
    return licenses
  }

  /**
   * Gets the licenses to delete from the current user selected,
   * this method is called from 'AdminLicensesList'.
   * At last, emits the event to display the confirmation modal.
   *
   * @param licenses to release
   */
  async showDeleteLicensesModal (licenses: License[]) {
    this.fromAdminLicensesList = true
    this.deviceLicensesToRelease = licenses
    this.$bvModal.show('delete-licenses-modal')
  }

  /**
   * Display the modal to confirm the devices elimination
   *
   * @param devices to eliminate
   */
  async showDeleteDeviceModal (devices: Device[]) {
    this.currentAction = CrudActions.READ
    this.devicesToUnlock = devices
    this.$bvModal.show('delete-devices-licenses-modal')
  }

  /**
   * After unlock licenses from a device updates its status to 'Inactivo' (INACTIVE)
   * and clear its licenses info
   *
   * @param updateComponent
   */
  licensesReleased (updateComponent: boolean) {
    // this flag is in case the event come from the license list component (historial)
    // and we need to update the licenses info
    this.updateAdminLicensesList = updateComponent
    let deviceIndex = this.getDeviceIndex(this.selectedItem)
    // set the status as INACTVE
    this.updateStatusColumnGrid(this.selectedItem, 'Inactivo', deviceIndex)
    // clean the licenses related to the device
    this.updateLicenseRelated(this.selectedItem, [], deviceIndex)
  }

  /**
   * After reset a device updates its status to 'Eliminado' (RESETTLE),
   * and clear its licenses info
   */
  deviceReseted () {
    let deviceIndex = this.getDeviceIndex(this.selectedItem)
    // set the status as RESETTLE
    this.updateStatusColumnGrid(this.selectedItem, 'Eliminado', deviceIndex)
    // clean the licenses related to the device
    this.updateLicenseRelated(this.selectedItem, [], deviceIndex)
  }

  /* Grid and action events */

  /**
   * Set selected row item as 'selectedItem'
   * @param item - row table
   */
  selectItem (item: DeviceCatalogGridItem) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.DELETE:
      case CrudActions.LICENSES:
        this.selectedItem = item
        break
    }
  }

  /**
   * Prepare selected item and crud action
   * @param item - row table
   */
  selectItemAndShowDetail (item: any) {
    this.selectItem(item)
    this.queryItem()
  }

  /**
   * Checks if the param item is the current selected
   * @param item - row table
   */
  isItemSelected (item: any) {
    return this.selectedItem === item
  }

  /**
   * Reset item selected to new empty
   */
  cleanItemSelected () {
    this.selectedItem = new DeviceCatalogGridItem()
  }

  /**
   * Set crud action to display form view
   */
  queryItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.READ_DETAIL
    }
  }

  /**
   * Creates a Device instance from the item grid currently selected
   */
  getDeviceFromItemSelected () {
    let device = new Device()
    device.id = this.selectedItem.id
    device.name = this.selectedItem.name
    switch (this.selectedItem.status) {
      case 'Activo':
        device.status = DeviceStatus.active
        break
      case 'Inactivo':
        device.status = DeviceStatus.inactive
        break
      case 'Eliminado':
        device.status = DeviceStatus.resettle
        break
    }
    device.uuid = this.selectedItem.uuid
    let creationDate = this.selectedItem.creation.split('/')
    let updateDate = this.selectedItem.update.split('/')
    device.creationDate = new Date(
      parseInt(creationDate[2]),
      parseInt(creationDate[1]) - 1,
      parseInt(creationDate[0]))
    device.updateDate = new Date(
      parseInt(updateDate[2]),
      parseInt(updateDate[1]) - 1,
      parseInt(updateDate[0]))
    return device
  }

  /**
   * Sets the licenses list to unlock from the device selected
   * and display the licenses modal to continue with the process
   */
  showUnlockLicensesModal () {
    this.deviceLicensesToRelease = this.selectedItem.activeLicenses
    this.$bvModal.show('delete-licenses-modal')
  }

  /**
   * Generates the Device instance based on the current item selected
   * and displays the modal message confirmation
   */
  showResetDeviceModal () {
    this.devicesToUnlock = [this.getDeviceFromItemSelected()]
    this.$bvModal.show('delete-devices-licenses-modal')
  }

  /**
   * Call deleteDeviceById request API to delete a device
   * @param id - device id to delete
   * @param name - device name to delete
   */
  deleteItemById (id: number, name: string) {
    catalogsService.deleteDeviceById(id).then((response) => {
      this.updateStatusColumnGrid(this.selectedItem, 'Eliminado', this.getDeviceIndex(this.selectedItem))
      let message = `El dispositivo ${id} - ${name} se ha eliminado correctamente.`
      this.$bvToast.toast(message, {
        title: 'El dispositivo se ha eliminado',
        variant: 'success',
        toaster: 'b-toaster-top-center',
        solid: true,
        autoHideDelay: 4000
      })
    }).catch((error) => {
      let messageValues = {
        title: 'Error al eliminar el dispositivo',
        message: '',
        delay: 10000,
        error: error
      }
      new MessageError({ propsData: messageValues }).displayMessage()
    }).finally(() => {
      this.currentAction = CrudActions.READ
    })
  }

  /**
   * Create a row table to show device info
   * @param origin - device instance to get actual values
   * @param target - grid item instance for table row
   */
  fillGridItem (origin: Device, target: DeviceCatalogGridItem) {
    Object.assign(target, origin)
    target.status = Device.getStatus(origin.status)
    target.creation = origin.creationDate ? new DateFormat(origin.creationDate).toDateString() : '- - / - - / - - - -'
    target.update = origin.updateDate ? new DateFormat(origin.updateDate).toDateString() : '- - / - - / - - - -'
    return target
  }

  /**
   * Updates ths user info column for each grid item
   * This is handles in n-batches of promises
   *
   * @param startIndex the first item to get
   * @param batchSize size for each batch of promises
   */
  async fillUserGridColumn (startIndex: number, batchSize: number) {
    let devicePromises: Promise<any>[] = []
    let lastIndex = startIndex
    for (let deviceIndex = startIndex; deviceIndex < this.items.length && deviceIndex < startIndex + batchSize; deviceIndex++, lastIndex++) {
      let device = this.items[deviceIndex]
      let request = this.prepareRequestForActiveLicenses(device.id)
      let devicePromise = this.getLicensesFromDevice(device, request)
      devicePromises.push(devicePromise)
    }

    await Promise.allSettled(devicePromises)
      .then(() => {
        if (lastIndex >= this.items.length) {
          return
        }
        this.fillUserGridColumn(lastIndex, batchSize)
      })
  }

  /**
   * Given a device and a new status updates the record info in the items
   *
   * @param device to update status
   * @param status to set
   * @param deviceIndex to insert
   */
  updateStatusColumnGrid (device: DeviceCatalogGridItem, status: string, deviceIndex: number) {
    device.status = status
    this.items.splice(deviceIndex, 1, device)
  }

  /**
   * Updates device license column in the grid
   *
   * @param device to update
   * @param licenses to set
   * @param deviceIndex form the current list of items
   */
  updateLicenseRelated (device: DeviceCatalogGridItem, licenses: License[], deviceIndex: number) {
    device.activeLicenses = licenses
    device.user = device.activeLicenses.map(license => license.user.name).join(',')
    this.items.splice(deviceIndex, 1, device)
  }

  /** @returns the device index in the current items */
  getDeviceIndex (device: DeviceCatalogGridItem) {
    return this.items.findIndex(it => it.id === device.id)
  }

  /** Sets the actions display the licenses */
  showLicenses () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.LICENSES
    }
  }

  /* Filter and sorting data */

  /**
   * Reset filter and paginator values
   */
  cleanFilter () {
    this.resetPaginator()
    this.filters = new DeviceCatalogFilters()
    this.loadData(true)
  }

  /**
   * Apply all filters by name and/or status.
   */
  filterItems () {
    this.resetPaginator()
    this.loadData(true)
  }

  /**
   * Checks if the current row index must be render on the table data
   * @param rowIndex - to verify
   */
  isRowVisibleInPage (rowIndex: number) {
    return rowIndex >= this.itemsVisibleRange[0] && rowIndex < this.itemsVisibleRange[1]
  }

  /**
   * Sort row for a column specified
   * @param col - with witch are going to order the rows
   */
  sortItems (col: CatalogGridItemColumn) {
    catalogsService.sortItems(this.items, col)
  }

  /**
   * Calculates new index range to display rows
   * @param page - current selected on paginator
   */
  onPageChange (page: number) {
    this.currentPage = page
    this.loadData(false)
  }

  /**
   * Clear paginator values
   */
  resetPaginator () {
    this.paginatorKey = new Date().getTime()
    this.currentPage = 1
  }

  /* Form */

  /**
   * Set current action to display list view
   */
  onCancelForm () {
    this.currentAction = CrudActions.READ
  }

  /* Export data */

  async exportData () {
    this.allDeviceItems = []
    let pageRequest = this.devicePageRequest()
    pageRequest.size = this.PAGE_SIZE
    pageRequest.page = 0
    await this.loadAllDevices(pageRequest)
      .then(() => {
        this.printCsv()
      })
  }

  printCsv () {
    let csv = 'ID,NOMBRE,ESTADO,UUID,CREACION,ACTUALIZACION\n'

    for (let it of this.allDeviceItems) {
      csv += `${reportsService.formatNullableTextForCsvFile(it.id)},`
      csv += `${reportsService.formatTextForCsvFile(it.name)},`
      csv += `${reportsService.formatTextForCsvFile(it.status)},`
      csv += `${reportsService.formatTextForCsvFile(it.uuid)},`
      csv += `${reportsService.formatTextForCsvFile(it.creation)},`
      csv += `${reportsService.formatTextForCsvFile(it.update)}\n`
    }

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `dispositivos.csv`
    element.click()
  }
}
</script>

<style scoped>
.domain-filter-info {
  line-height: 1;
  margin: 0;
  opacity: 50%;
  font-style: italic;
  font-size: small;
}
</style>
